import { useEffect, useEffectEvent, useState, lazy, Suspense, useCallback } from "react";
import { useLocation, useNavigate, Routes, useSearchParams, createSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { getCheckIfAuthenticated, getUserDetails } from "./actions/userDetails";
import { hideChatBot, hideRecaptchaBadge, showChatBot } from "@Services/utils/helpers";
import LicenceWarning from "./components/LicenceWarning/LicenceWarning";
import { getPortalSetting, getUserPermissions } from "./actions/userPermissions";
import { fetchLayout } from "./actions/layout";
import { settleLayoutStatus } from "./reducers/layout";
import MinimizeFloatter from "@Components/MinimizeFloatter";
import { getPortalData } from "@Actions/portalSetting";
import * as Sentry from "@sentry/react";
import { identifyPostHog } from "./services/posthog";
import OldBrowserWarning from "@Components/OldBrowserWarning";
import MobileDeviceRestrict from "@Components/MobileDeviceRestrict";
import { getCurrProjectData } from "./actions/breadcrumbsdata";
import axiosWrapper from "@AxiosWrapper";
import StickySidebar from "./components/StickySidebar";
import { setShowStickySidebar, setSidebarSelectedCell } from "./reducers/stickySidebar";
import ErrorBoundary from "./ErrorBoundary";
import routes from "./routes";
import { routeElements } from "./routeElements";
import { getPortalCustomModules } from "@Actions/portalSetting.helper";
import { setPreferredLanguage } from "./reducers/preferredLanguage";
import get from "lodash/get";
import SplitLayout from "@CubeComponents/html/SplitLayout/SplitLayout";
import { setRenderHeader } from "./reducers/portalSetting";
import { addRedirectParam, SIGNUP_ROUTE } from "@Helpers/urlHelpers";
import { setSigninData } from "./reducers/signinData";
import { objectIdString } from "@Helpers/schemas/common.schema";
import Text from "@CubeComponents/html/Text/Text";
import Forbidden from "@Pages/Error/Forbidden";
import { CircularProgress } from "@mui/material";
import { RootState } from "@Reducers/index";
import { PORTAL_NO_SEAT } from "@Helpers/constants";
import { isProductPlanCode } from "@Helpers/cubeAi";
import { setBreadCrumbData, setProjectData } from "@Reducers/breadcrumbsdata";
import { t } from "@i18n";
import { useUiNextGate, useViewerOptIn } from "@Utils/uiNext";
import { findRoute, getRouteKind, routeShowsHeader } from "@Components/AppShellNext/routeKind";
import { shouldShowUiInvite } from "@Utils/uiInvite";
import { projectGate } from "@Utils/projectGate";

const FeedbackToastComponent = lazy(() => import("./cubeComponent/html/feedbackToast"));
const FileSync = lazy(() => import("@Features/files/components/FilesSync"));
const PortalIllustration = lazy(() => import("@Features/portal/components/PortalIllustration"));
const Header = lazy(() => import("@Components/Header"));
const HelpChat = lazy(() => import("@Components/HelpChat"));
import { isCubeAiAvailable, isCubeAiEntitled } from "@Components/HelpChat/availability";
import { isNonZero } from "@Helpers/numberCondition";
const FileUpload = lazy(() => import("@Features/files/components/FileManager/FileUpload/FileUpload"));
// Both mount only once a user is signed in, so neither belongs on the critical path of the sign-in page or
// the first paint. Imported statically, their whole trees (the chat socket client, luxon) would load on
// every page.
const Notification = lazy(() => import("./components/Notification"));
const PortalChatPanel = lazy(() => import("@Features/portalChat/components/PortalChatPanel"));
// The new UI shell (plan-ds §4.2): loaded only while the flag is on, so the classic bundle is unchanged.
const AppShellNext = lazy(() => import("@Components/AppShellNext/AppShellNext"));
const NextGlobalHosts = lazy(() => import("@Components/AppShellNext/NextGlobalHosts"));
// The classic → new UI invite (plan notes ui-invite): loaded only while it shows, never outside ALLOWED_ENVS.
const UiInvite = lazy(() => import("@Components/AppShellNext/invite/UiInvite"));

const matchRoute = (pathname: string, path: string) => {
  const regex = new RegExp("^/?" + path.replaceAll(/:[^\s/]+/g, String.raw`([\w-]+)`) + "$");
  return regex.test(pathname);
};

const modulesMapper: Record<string, string> = {
  measurements: "https://support.cube.ms/ebd//22dde45767ab8195b034c62e74458b73",
  general: "https://support.cube.ms/ebd//253de45767ab80f49c72d6992a5610e2?v=266de45767ab802ba907000cc6841787",
};

/** Boots the Sleekplan feedback widget for the signed-in user. Reads nothing from the component. */
const initSleekPlan = async () => {
  const getSleekToken = await axiosWrapper.api.get("/accounts/getSleekToken", {});
  if (getSleekToken.data.status) {
    const token = getSleekToken.data.result.token;
    if (window.SLEEK_LOADED) {
      if (typeof window.$sleek?.setUser === "function") {
        window.$sleek.setUser({ token });
      }
      return;
    }

    window.$sleek = [];
    window.SLEEK_PRODUCT_ID = 401112407;

    const script = document.createElement("script");
    script.src = "https://client.sleekplan.com/sdk/e.js";
    script.async = true;

    document.head.appendChild(script);
    window.SLEEK_LOADED = true;

    window.document.addEventListener(
      "sleek:init",
      () => {
        if (typeof window.$sleek?.setUser === "function") {
          window.$sleek.setUser({ token });
        }
      },
      { once: true },
    );
  }
};

function Router() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const licenceStatus = useSelector((state: RootState) => state.licenceStatus);
  const userDetails = useSelector((state: RootState) => state.userDetails);

  // fileSyncState,
  const showStickySidebar = useSelector((state: RootState) => state.showStickySidebar);

  const fileUploadState = useSelector((state: RootState) => state.fileUploadState);
  const helpSectionState = useSelector((state: RootState) => state.helpSectionState);
  const renderHeader = useSelector((state: RootState) => state.renderHeader);
  const portalData = useSelector((state: RootState) => state.portalData);
  const checkIfAuthenticated = useSelector((state: RootState) => state.checkIfAuthenticated);
  const signinData = useSelector((state: RootState) => state.signinData);
  const projectData = useSelector((state: RootState) => state.projectData);
  const memoryLimitEnabled = useSelector((state: RootState) => {
    const settings = state.GetSettingModalData;
    const limitMiB = settings.ModelLoading_memoryLimit ? Number(settings.ModelLoading_memoryLimitValue) : 0;
    return Number.isFinite(limitMiB) && limitMiB > 0;
  });

  const [isPortalExpired, setIsPortalExpired] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const uiGate = useUiNextGate();
  const uiNext = uiGate === "on";
  const viewerOptIn = useViewerOptIn();
  // Nothing of the new shell is computed while the new UI is off (review F69): no route-kind or route-flag scan.
  const routeKind = uiGate === "off" ? null : getRouteKind(location.pathname, location.search);
  // One switch for every classic overlay and every next host (plan-ds §4.2): exactly one set mounts. It reads the
  // route's `showHeader` in render, not `renderHeader` (set one effect later), so a navigation never renders the
  // new route in the wrong tree first.
  const shellRoute =
    routeKind !== null &&
    !location.pathname.includes("pdfExport") &&
    !location.pathname.startsWith("/share/") &&
    (routeShowsHeader(location.pathname, routes) || (routeKind === "viewer" && viewerOptIn));
  const showNextShell = uiNext && shellRoute;
  // A stored "next" record waiting for this session's user (review F09): a shell route renders neither tree (and no
  // classic overlay) until the user is known, so another user's record never mounts the new UI, nor this user's
  // record the classic one first.
  const shellPending = uiGate === "pending" && shellRoute;
  const nextShellActive = showNextShell && !isPortalExpired;
  const classicChromeOff = nextShellActive || shellPending;
  // False before anything is read outside ALLOWED_ENVS (production): no route scan, no storage read.
  const showUiInvite = shouldShowUiInvite({
    gate: uiGate,
    signedIn: Boolean(userDetails.id && portalData._id),
    portalExpired: isPortalExpired,
    projectReady: projectGate(searchParams.get("projectId"), projectData) === "ready",
    pathname: location.pathname,
    route: () => findRoute(location.pathname, routes),
  });
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [showLogoutError, setShowLogoutError] = useState("");
  // Feature detection, not a preference: the result is fixed for the lifetime of the document, so it
  // is computed once at mount and never updated.
  const [showOldBrowserWarning] = useState(
    () => typeof Array.prototype.at != "function" || typeof Array.prototype.toSorted != "function",
  );
  const errorMessageParam = searchParams.get("errorMessage");
  useEffect(() => {
    if (errorMessageParam) {
      // Updater form: the param is dropped from the params as they are when the update runs. A run caused
      // only by `setSearchParams` changing identity (it follows `searchParams`) finds no message and does nothing.
      setSearchParams((currentSearchParams) => {
        const newSearchParams = new URLSearchParams(currentSearchParams);
        newSearchParams.delete("errorMessage");
        return newSearchParams;
      });
      setShowLogoutError(decodeURIComponent(errorMessageParam));
    }
  }, [errorMessageParam, setSearchParams]);

  const projectIdParam = searchParams.get("projectId");
  useEffect(() => {
    // URL query params are user-supplied, so the id is validated rather than asserted here; anything
    // that is not a 24-hex ObjectId takes the same "no project in scope" path as an absent param.
    const projectId = objectIdString.safeParse(projectIdParam);
    if (projectId.success) {
      // Fire-and-forget: the thunk publishes the project through the store; nothing here reads its result.
      void dispatch(getCurrProjectData({ id: projectId.data, originSource: "PROJECT" }));
    } else {
      dispatch(setProjectData({ isDataFetched: false }));
    }
  }, [projectIdParam]);

  useEffect(() => {
    const isProjectScopedRoute = location.pathname.startsWith("/project");
    if (!projectData._id && isProjectScopedRoute) {
      const breadCrumb = [
        {
          text: t("Projects"),
          link: "/projects",
        },
      ];
      dispatch(setBreadCrumbData(breadCrumb));
    }
  }, [projectData._id, location.pathname]);

  // An effect event: it reads the user, portal and route as they are when it runs, while only the effect's own
  // triggers below decide WHEN it runs (a query-string change alone must not re-post /getSigninData).
  const validateReturnRouter = useEffectEvent(async () => {
    if (userDetails.id) {
      if (
        portalData.isExpired &&
        !location.pathname.includes(SIGNUP_ROUTE) &&
        location.pathname != "/newInvitation" &&
        userDetails.portal?.id
      ) {
        setIsPortalExpired(true);
      } else if (
        !userDetails.portal?.id &&
        !["/", SIGNUP_ROUTE, "/signupflow", "/newInvitation"].includes(location.pathname)
      ) {
        const signinData = await axiosWrapper.api.post("/accounts/getSigninData", {});
        // A failed /getSigninData leaves no `frontendUrl` to compare against, and the mismatch that
        // follows is load-bearing: the user is sent back to the root portal rather than left on a
        // host that could not be confirmed.
        const frontendUrl = signinData.data.status ? signinData.data.frontendUrl : undefined;
        if (globalThis.location.host != frontendUrl) {
          const newUrl = addRedirectParam({
            targetPath: new URL("/", globalThis.location.origin),
            // `AddRedirectParamOptions.redirectTo` is the DOM `Location`, not react-router's. Only
            // pathname/search/hash are read, so hand it a real `URL` built from the current route.
            redirectTo: new URL(`${location.pathname}${location.search}${location.hash}`, globalThis.location.origin),
          });
          // Fire-and-forget: react-router's navigate settles after the transition; nothing follows it here.
          void navigate(newUrl);
        }
      } else if (portalData.newSignUpFlow && portalData.subscription === undefined) {
        // The billing has not reached the slice yet (it follows sign-in); the seat check waits for it.
        return;
      } else {
        const planLineItems = portalData.subscription?.lineItems ?? [];
        // A CUBE AI seat alone never opens the portal (CP-1361), so only product plans count here.
        const planCodes: string[] = planLineItems
          .filter((i) => i.type === "plan")
          .map((i) => i.code)
          .filter(isProductPlanCode);
        if (!planCodes.length) {
          const fallback = portalData.subscription?.plan ?? "";
          if (fallback && isProductPlanCode(fallback)) planCodes.push(fallback);
        }
        const seat = planCodes
          .map((code: string) => get(userDetails, `seat.${code}`))
          .find((s: unknown) => s !== undefined && s !== null && s !== PORTAL_NO_SEAT);
        const portalRole = get(userDetails, "portalRole.id");
        if (portalData.newSignUpFlow && !isNonZero(seat) && portalRole) {
          setIsPortalExpired(true);
        } else {
          setIsPortalExpired(false);
        }
      }
    } else {
      setIsPortalExpired(false);
    }
  });

  useEffect(() => {
    // Fire-and-forget: the check redirects or flips state on its own; this effect has nothing to wait for.
    void validateReturnRouter();
  }, [portalData, location.pathname, userDetails.id]);

  useEffect(() => {
    if (location.pathname.includes("null") || location.search.includes("null")) {
      globalThis.location.href = "/";
    }
  }, [location]);

  const getSigninData = useCallback(async () => {
    const signinData = await axiosWrapper.api.post("/accounts/getSigninData", {});
    dispatch(setSigninData({ data: signinData.data }));
  }, []);

  useEffect(() => {
    dispatch(getCheckIfAuthenticated());
    // Fire-and-forget: the result lands in the store via `setSigninData`, which other effects read.
    void getSigninData();
  }, [getSigninData]);

  useEffect(() => {
    if (userDetails.id && portalData._id && userDetails.portal?.id) {
      dispatch(getCheckIfAuthenticated());
      // Fire-and-forget: the thunk publishes portal settings through the store.
      void dispatch(getPortalSetting());
      // Fire-and-forget: reloads the portal slice with the billing only a signed-in member is sent.
      void dispatch(getPortalData());
    }
  }, [userDetails.id, portalData._id, userDetails.portal?.id]);

  const checkIfAuthenticatedStatus = get(checkIfAuthenticated, "data.status");
  // `checkIfAuthenticated` stays null until /checkIfAuthenticated has answered.
  const isAuthenticationChecked = checkIfAuthenticated !== null;

  useEffect(() => {
    if (isAuthenticationChecked) {
      if (checkIfAuthenticatedStatus) {
        dispatch(
          getUserPermissions({
            resourceType: ["RESOURCE_TYPE_PORTAL"],
          }),
        );
        hideRecaptchaBadge();
        hideChatBot();
      } else {
        setIsPortalExpired(false);
        showChatBot();
      }
    }
  }, [isAuthenticationChecked, checkIfAuthenticatedStatus]);

  const userDetailsPortalId = get(userDetails, "portal.id");
  useEffect(() => {
    if (checkIfAuthenticatedStatus && (!userDetails.id || !userDetailsPortalId)) {
      dispatch(getUserDetails());
    }
  }, [checkIfAuthenticatedStatus, userDetails.id, userDetailsPortalId]);

  useEffect(() => {
    if (userDetails.id && portalData._id && userDetails.portal?.id) {
      // Fire-and-forget: the thunk publishes the custom modules through the store.
      void dispatch(getPortalCustomModules());
    }
  }, [userDetails.id, portalData._id, userDetails.portal?.id]);

  useEffect(() => {
    if (userDetails.id) {
      if (process.env.REACT_APP_SENTRY_DSN) {
        // Sentry's `User` declares `email?: string`, so the key is omitted rather than sent as
        // `undefined`; Sentry treats an absent and an explicitly-undefined key identically.
        Sentry.setUser({ ...(userDetails.email !== undefined && { email: userDetails.email }), id: userDetails.id });
      }
      identifyPostHog(userDetails);
      // if cypress then do not call
      // `Cypress` is injected by the Cypress runner at runtime and has no ambient declaration in
      // this package. Asserted rather than declared globally so the fake stays local to its use.
      if (
        !(globalThis as typeof globalThis & { Cypress?: unknown }).Cypress &&
        !globalThis.location.pathname.includes("pdfExport")
      ) {
        // Fire-and-forget: boots the third-party feedback widget; no caller waits on it. Read off
        // `globalThis.location` rather than the router's: the PDF export routes are always a full
        // document load, and a reactive read here would re-run this effect on every navigation.
        void initSleekPlan();
      }
    }
  }, [userDetails]);

  useEffect(() => {
    if (
      checkIfAuthenticatedStatus &&
      signinData?.data?.status &&
      globalThis.location.host == signinData.data.frontendUrl &&
      signinData.data.EnableMultiPortal &&
      ![SIGNUP_ROUTE, "/signupflow", "/reset-password", "/"].includes(location.pathname)
    ) {
      const newUrl = addRedirectParam({
        targetPath: new URL("/", globalThis.location.origin),
        // `AddRedirectParamOptions.redirectTo` is the DOM `Location`, not react-router's. Only
        // pathname/search/hash are read, so hand it a real `URL` built from the current route.
        redirectTo: new URL(`${location.pathname}${location.search}${location.hash}`, globalThis.location.origin),
      });
      // Fire-and-forget: last statement of the effect; nothing sequences after the transition.
      void navigate(newUrl);
    }
  }, [checkIfAuthenticatedStatus, signinData, location.pathname, location.search, location.hash]);

  const updateLanguage = useCallback(async () => {
    const response = await axiosWrapper.api.post("/accounts/get", {
      projection: ["preferredLanguage"],
    });
    // /accounts/get is projection-driven: it returns only the keys named in `projection`, so
    // `preferredLanguage` is optional even on the success arm and the "en" fallback is live.
    const newLanguage = (response.data.status ? response.data.result.preferredLanguage : undefined) ?? "en";
    // ProviderLanguage flips <html dir> from the store for every RTL language.
    dispatch(setPreferredLanguage(newLanguage));
  }, []);

  useEffect(() => {
    if (checkIfAuthenticatedStatus) {
      // Fire-and-forget: the thunk publishes the layout through the store.
      void dispatch(fetchLayout());
      // Fire-and-forget: the language lands in the store (and so on <html dir>); nothing here waits on it.
      void updateLanguage();
    } else if (isAuthenticationChecked) {
      dispatch(setPreferredLanguage("en"));
      // Signed out: no layout will be fetched, so it is no longer pending (the shell's search chip waits on it).
      dispatch(settleLayoutStatus("failed"));
    }
  }, [isAuthenticationChecked, checkIfAuthenticatedStatus, updateLanguage]);

  useEffect(() => {
    // Re-checked on every navigation: the exclusions below depend on the current URL.
    if (!globalThis.location.host.includes("dpmsportal")) {
      if (
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) &&
        !globalThis.location.href.includes("mobileExplorer") &&
        !globalThis.location.href.includes(SIGNUP_ROUTE) &&
        !globalThis.location.href.includes("newSignIn") &&
        !globalThis.location.href.includes("reset-password") &&
        !globalThis.location.href.includes("newInvitation") &&
        // Shareable external links (CP-283): a recipient has no CUBE account and no app to install —
        // the share pages ARE the experience, so the "use the app" screen would leave them nothing.
        !globalThis.location.pathname.startsWith("/share/") &&
        globalThis.location.pathname !== "/"
      ) {
        setIsMobileDevice(true);
      } else {
        setIsMobileDevice(false);
      }
    }
  }, [location]);

  useEffect(() => {
    if (location.pathname != "/") {
      setShowLogoutError("");
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!location.pathname.includes("googleSigninButton")) {
      // Fire-and-forget: the thunk publishes the portal through the store.
      void dispatch(getPortalData());
    }
  }, [location.pathname]);

  useEffect(() => {
    if (document.getElementById("sleek-widget-wrap") && typeof window.$sleek?.close === "function") {
      window.$sleek.close();
    }
  }, [location.pathname]);

  useEffect(() => {
    const currentRoute = routes.find((route: (typeof routes)[number]) =>
      route.path.some((path: string) => matchRoute(location.pathname, path)),
    );
    if (currentRoute?.showStickySidebar === false) {
      dispatch(setShowStickySidebar(false));
      dispatch(setSidebarSelectedCell(""));
    }
    if (currentRoute?.showHeader === false) {
      dispatch(setRenderHeader(false));
    } else {
      dispatch(setRenderHeader(true));
    }
    if (location.pathname === "/" && checkIfAuthenticatedStatus) {
      return;
    }
    if (
      location.pathname !== "/" &&
      isAuthenticationChecked &&
      !checkIfAuthenticatedStatus &&
      !location.pathname.includes("thank-you") &&
      !location.pathname.includes("resetPassword") &&
      !location.pathname.includes("95789") &&
      !location.pathname.includes(SIGNUP_ROUTE) &&
      !location.pathname.includes("invitation") &&
      !location.pathname.includes("pdfExport") &&
      !location.pathname.includes("newSignIn") &&
      !location.pathname.includes("reset-password") &&
      !location.pathname.includes("newInvitation") &&
      // Shareable external links (CP-283): the recipient has no CUBE account by design, so the
      // generic bounce-to-signin would replace the share with a login screen. The page owns its own
      // no-session states (password gate / invalid link).
      !location.pathname.startsWith("/share/")
    ) {
      // Fire-and-forget: last statement of the effect; nothing sequences after the bounce to signin.
      void navigate({
        pathname: "/",
        search: createSearchParams({ redirectUrl: location.pathname + location.search }).toString(),
        hash: location.hash,
      });
    }
  }, [location.pathname, isAuthenticationChecked, checkIfAuthenticatedStatus, location.hash, location.search]);

  // An effect event: the module is read as it is when `display` changes, and a change of `module` alone opens
  // nothing (profileSideBar dispatches `display: true` without flipping it).
  const openHelpSection = useEffectEvent(() => {
    const url = modulesMapper[helpSectionState.module] || modulesMapper["general"];
    window.open(url, "_blank");
  });

  useEffect(() => {
    if (helpSectionState.display !== null) {
      openHelpSection();
    }
  }, [helpSectionState.display]);

  const getContent = () => {
    const projectId = searchParams.get("projectId");
    // Routes / Forbidden / spinner for every tree below: a route renders only once the slice holds ITS project, not
    // the previous one still stored during a switch.
    const gate = projectGate(projectId, projectData);
    const routesContent = (
      <ErrorBoundary location={location} dispatch={dispatch} memoryLimitEnabled={memoryLimitEnabled}>
        <Routes>{routeElements(routes)}</Routes>
      </ErrorBoundary>
    );

    if (location.pathname.includes("pdfExport")) {
      return gate === "ready" ? (
        routesContent
      ) : gate === "forbidden" ? (
        <Forbidden />
      ) : (
        <div className="h-full flex justify-center w-full items-center">
          <CircularProgress />
        </div>
      );
    }

    if (shellPending) return null;

    if (showNextShell) {
      // The same gate as the classic tree below (routes / Forbidden / spinner), not a new rule.
      return (
        <Suspense fallback={null}>
          <AppShellNext kind={routeKind} hasNav={Boolean(showStickySidebar) || routeKind === "viewer"} gate={gate}>
            {routesContent}
          </AppShellNext>
        </Suspense>
      );
    }

    return (
      <SplitLayout
        initialSizes={[100, 0]}
        gutterWidth={"0.125rem"}
        minSizes={[10, 20]}
        style={{ display: "flex", height: "100%" }}
        showProperties={true}
      >
        {[
          <div key={"mainContent"} className="flex flex-col overflow-auto h-full w-full">
            {renderHeader && (
              <Suspense fallback={<></>}>
                <Header />
              </Suspense>
            )}
            {gate === "ready" ? (
              <div className="flex overflow-auto h-full w-full">
                {/* Every sidebar entry links into the `?projectId` project, and a route without one can still
                    carry a stale `showStickySidebar` (e.g. `/issuesList?byPortal=true` reached by Back from a
                    project page, which never clears it), so the sidebar mounts only with a project to link into.
                    ⚠️ Its `useStickySidebar` is also the only writer of the module registry that
                    `useUserModuleAccess().ready` waits for, so a page gated on it renders only with a `?projectId`.
                    Every /project/:projectId URL has one, because RouteParamGuard adds it; the portal-wide lists
                    skip the gate under `?byPortal=true`, which RouteParamGuard adds when they have no project. */}
                {showStickySidebar && projectId ? <StickySidebar projectId={projectId} /> : null}
                {/* `routeOutlet`: the mobile layout checker judges "the page rendered" by this box alone,
                    so the header and sidebar around it never pass for a page. */}
                <div className="w-full h-full overflow-auto" data-testid="routeOutlet">
                  {routesContent}
                </div>
              </div>
            ) : gate === "forbidden" ? (
              <Forbidden />
            ) : (
              <div className="h-full flex justify-center w-full items-center">
                <CircularProgress />
              </div>
            )}
          </div>,
        ].filter(Boolean)}
      </SplitLayout>
    );
  };
  return (
    <>
      {showLogoutError && (
        <div className="flex justify-around text-white p-1 bg-system_colour_3">
          <Text>{showLogoutError}</Text>
        </div>
      )}
      {!location.pathname.includes("pdfExport") && (
        <Suspense fallback={<></>}>
          <Suspense fallback={<></>}>
            <FeedbackToastComponent />
          </Suspense>
        </Suspense>
      )}

      {showOldBrowserWarning && (
        <Suspense fallback={<></>}>
          <OldBrowserWarning />
        </Suspense>
      )}

      {isMobileDevice && (
        <Suspense fallback={<></>}>
          <MobileDeviceRestrict />
        </Suspense>
      )}

      <Suspense fallback={<></>}>
        {userDetails.id && portalData._id && (
          <Suspense fallback={<></>}>
            <FileSync />
          </Suspense>
        )}
      </Suspense>
      {/* `h-dvh`, not `h-screen`: on a real phone 100vh is the height with the browser's bars HIDDEN, so
          while they show, the bottom of a full-height page (the PDF toolbar) sits behind them — and the
          viewer's stylesheet turns body scrolling off, so it cannot be reached. Identical on desktop. */}
      <div className={`${!location.pathname.includes("pdfExport") ? "flex h-dvh flex-col overflow-auto" : ""}`}>
        {!licenceStatus && <LicenceWarning />}
        {!classicChromeOff && <MinimizeFloatter />}
        {userDetails.id && portalData._id && !classicChromeOff && (
          <Suspense fallback={<></>}>
            <Notification />
            <PortalChatPanel />
          </Suspense>
        )}
        {!classicChromeOff &&
          userDetails.id &&
          portalData._id &&
          !location.pathname.includes("pdfExport") &&
          isCubeAiAvailable(location.pathname) &&
          isCubeAiEntitled(portalData, userDetails) && (
            <Suspense fallback={<></>}>
              <HelpChat />
            </Suspense>
          )}
        {(fileUploadState == "MAXIMISED" || fileUploadState == "MINIMISED") && (
          <Suspense fallback={<></>}>
            <FileUpload />
          </Suspense>
        )}
        {!isPortalExpired &&
          (uiGate !== "off" ? (
            <Suspense fallback={null}>
              <NextGlobalHosts enabled={nextShellActive && Boolean(userDetails.id && portalData._id)}>
                {getContent()}
              </NextGlobalHosts>
            </Suspense>
          ) : (
            getContent()
          ))}
        {isPortalExpired && (
          <Suspense fallback={<></>}>
            <Suspense fallback={<></>}>
              <PortalIllustration />
            </Suspense>
          </Suspense>
        )}
        {showUiInvite && (
          <Suspense fallback={null}>
            <UiInvite />
          </Suspense>
        )}
      </div>
    </>
  );
}

export default Router;
